import {getBestName, isTruthy, naturalSort} from "../lib/utils";
import MoleculeImage from "../components/MoleculeImage";
import {MDCollapsibleList, MDCollapsibleText} from "../components/results-grid/grid-row/MDCollapsibleContent";
import EntityView from "../components/EntityView";
import {substituteAttributeValue} from "./interpolation";
import {markMatchingText} from "./utils";

// HGNC: is only sometimes redundant, so be careful to replace "HGNC:HGNC"
const REDUNDANT_PREFIXES = [
  "Ensembl:",
];

const STAR = "★";
const STAR_OUTLINE = "☆";


export const applyFilter = (obj, filter = "", filterArg = null,
                            uniqueValues, overrides = {},
                            interpolatedProps = {}, fieldName = null,
                            markText = null, quote = x => x) => {
  // Apply markText + quote _only_ to values wrapped by markdown/html
  const entity = interpolatedProps['entity'];
  if (!markText) {
    markText = x => x;
  }
  // `context` indicates any property values already interpolated for support of the `unique` filter
  if (obj instanceof Array && filter !== "sort" && filter !== "clist" && filter !== "ctext"
      && filter !== "noevidence") {
    return obj.map(el => applyFilter(el, filter, filterArg, uniqueValues, overrides, interpolatedProps, fieldName,
                                     markText));
  }
  const NOVALUE = "";
  const svalue = obj?.toString() || NOVALUE;
  if (typeof(filter) != "string") {
    filter = ""
  }
  try {
    // Filters with or without dashes are equivalent
    filter = filter.replaceAll("-", "");
  }
  catch(error) {
    filter = "";
  }
  let size = "";
  switch (filter) {
    case "aka":
      const matchField = entity['match-field'];
      if (matchField === "smiles" || matchField === "inchi" || matchField === "inchikey" || matchField === "id") {
        if (/\)$/.test(svalue)) {
          return svalue.replace(/\)$/, ` via ${matchField})`);
        }
        return `${svalue} (via ${matchField})`;
      }
      const matchTerm = entity['match-term'];
      const matchValue = entity['match-value'];
      if (matchField && matchTerm && matchValue && typeof matchValue === 'string') {
        if (markMatchingText(svalue, matchTerm) === svalue) {
          if (matchField === "description" || matchField === "abstract") {
            return `${svalue} (from ${matchField})`;
          }
          // Strip "uniprot:" prefix from matchValue for cleaner display
          let displayValue = matchValue;
          if (/^uniprot:/i.test(matchValue)) {
            displayValue = matchValue.replace(/^uniprot:/i, '');
          }
          // Apply marking to displayValue and return as HTML to prevent double-marking
          const markedMatchValue = markText(displayValue, {asString: true});
          return `<span>${svalue} (aka ${markedMatchValue})</span>`;
        }
      }
      return svalue;
    case "base64":
      return btoa(svalue);
    case "best":
      return getBestName(obj);
    case "brace":
    case "braces":
      return `{${svalue}}`;
    case "bracket":
    case "brackets":
      return `[${svalue}]`;
    case "capitalize":
      const first = svalue.charAt(0);
      if (first === first.toUpperCase()) return svalue;
      return svalue.charAt(0).toUpperCase() + svalue.slice(1).toLowerCase();
    case "category":
      return `<span class="category">${quote(svalue)}</span>`;
    case "class":
      if (svalue.startsWith("<")) {
        if (svalue.indexOf("className") !== -1) {
          return svalue.replace(/className=['"]([^'"]*)['"]/, `className="$1 ${filterArg}"`);
        }
        if (svalue.indexOf("/>") !== -1) {
          return svalue.replace("/>", ` className="${filterArg}" />`);
        }
        if (svalue.indexOf(">") !== -1) {
          return svalue.replace(/^([^>]+)>/, `$1 className="${filterArg}">`);
        }
      }
      return `<span class="${filterArg}">${svalue}</span>`;
    case "noevidence": {
      // Activity shape: {gene_symbol: {activity_label: [[title, desc], ...]}} straight off the
      // wire, but patchEntity.js converts entity.activity to Object.entries(...) -- an array of
      // [gene_symbol, {activity_label: [...]}] pairs -- before this template's search-result
      // string is interpolated, so `obj` arrives here already array-shaped, not the raw dict.
      // Genesets get this merged in unfiltered (PLEX-6118 -- no visible_target_symbols to narrow
      // it, unlike compounds), so a promiscuous linked compound can carry hundreds of
      // targets/evidence pairs into every row of a results page. Search-result rows only need
      // the labels, not the per-label evidence used to build the hover tooltip, so strip down
      // to gene_symbol -> [label1, label2, ...] rather than building (and rendering)
      // potentially huge tooltip HTML per row.
      const stripLabels = (labels) => Object.keys(labels || {});
      if (Array.isArray(obj)) {
        return obj.map(([symbol, labels]) => [symbol, stripLabels(labels)]);
      }
      if (obj && typeof obj === "object") {
        return Object.fromEntries(Object.entries(obj).map(([symbol, labels]) =>
          [symbol, stripLabels(labels)]));
      }
      return obj;
    }
    case "clist":
      // Make sure markdown knows how to render this component
      if (!overrides['MDCollapsibleList']) {
        overrides['MDCollapsibleList'] = {component: MDCollapsibleList, props: {markText, list: svalue}};
      }
      else {
        overrides['MDCollapsibleList'].props = {...(overrides['MDCollapsibleList'].props || {}), markText, list: svalue};
      }
      const listTitle = filterArg ? ` title="${filterArg}"` : "";
      return `<MDCollapsibleList ${listTitle}/>`;
    case "cpdimage":
    case "molimage":
      // Make sure markdown knows how to render this component
      if (!overrides['MoleculeImage']) {
        overrides['MoleculeImage'] = {component: MoleculeImage, props: {markText}};
      }
      else {
        overrides['MoleculeImage'].props = {...(overrides['MoleculeImage'].props || {}), markText};
      }
      const dimensions = filterArg ? ` width="${filterArg}" height="${filterArg}"` : "";
      // Use substituteAttributeValue to avoid HTML escaping of special chars in SMILES
      return `<MoleculeImage encoding={${substituteAttributeValue("encoding", svalue, interpolatedProps)}}${dimensions}/>`;
    case "ctext":
      // Make sure markdown knows how to render this component
      if (!overrides['MDCollapsibleText']) {
        overrides['MDCollapsibleText'] = {component: MDCollapsibleText, props: {markText, text: svalue}};
      }
      else {
        overrides['MDCollapsibleText'].props = {...(overrides['MDCollapsibleText'].props || {}), markText, text: svalue};
      }
      const textTitle = filterArg ? ` title="${filterArg}"` : "";
      return `<MDCollapsibleText ${textTitle} />`;
    case "default":
      return svalue !== NOVALUE ? svalue : filterArg;
    case "degrees":
      return svalue !== NOVALUE ? `${svalue}°` : "";
    case "dumps":
      return JSON.stringify(obj);
    case "fixed":
      try {
        const number = parseFloat((svalue || "0").replace(",", "."));
        const digits = parseInt(filterArg, 10) || 2;
        return number.toFixed(digits);
      } catch(e) {
        return svalue;
      }
    case "flag":
      const label = isTruthy(svalue) ? filterArg || svalue : NOVALUE;
      return `<div class="sr-desc">${markText(quote(label), true)}</div>`;
    case "entity":
      if (!overrides['Entity']) {
        overrides['Entity'] = {component: EntityView, props: {showLabel: true, markText}};
      }
      else {
        overrides['Entity'].props = {...(overrides['Entity'].props || {}), markText};
      }
      return `<Entity entity={${substituteAttributeValue("entity", obj, interpolatedProps)}} />`;
    case "fold":
      // Convert log2-fold to simple-fold
      return Math.round(Math.pow(2, Math.abs(obj)));
    case "int":
      return svalue.replace(/^(\d+).*/, '$1');
    case "label":
      const conj = svalue[0] === '>' ? "" : "= "
      switch(fieldName) {
        case "ec50":
          return `EC50 ${conj}${svalue}nM`;
        case "ic50":
          return `IC50 ${conj}${svalue}nM`;
        case "kd":
          return `Kd ${conj}${svalue}nM`;
        case "ki":
          return `Ki ${conj}${svalue}nM`;
        case "kh":
          return `KH ${conj}${svalue}`;
        case "kl":
          return `kL ${conj}${svalue}`;
        case "kon":
          return `kon ${conj}${svalue}`;
        case "koff":
          return `koff ${conj}${svalue}`;
        case "ph":
          return `pH ${svalue}`;
        default:
          return svalue;
      }
    case "list":
      return [svalue];
    case "longtext":
      return `<span class="long-text">${markText(quote(svalue), true)}</span>`;
    case "lower":
      return svalue.toLowerCase();
    case "nodataset":
      REDUNDANT_PREFIXES.forEach(prefix => {
        if (obj.startsWith(prefix)) {
          obj = obj.substring(prefix.length);
        }
      });
      return obj.replace(/^[-a-z]+:([-a-z]+:)?/, '').replace("HGNC:HGNC", "HGNC");
    case "noencoding":
      if (/^(smiles|inchi|query):/.test(svalue)) {
        return "";
      }
      return svalue;
    case "noprefix":
      const symb = filterArg || ":";
      const split = svalue.split(symb)
      return split[split.length-1];
    case "nosmiles":
      // Hide SMILES if the value starts with "smiles:" OR if the entity ID is a SMILES ID
      if (svalue.startsWith("smiles:")) {
        return NOVALUE;
      }
      // Check if entity ID indicates this is a SMILES-based entity
      if (entity && entity.id && /^(smiles|query):/.test(entity.id)) {
        return NOVALUE;
      }
      return svalue;
    case "patent":
      return svalue.replace(/^([A-Z]+)([0-9]+)(A-Z]+\d*)?/, "$1-$2-$3").replace(/-$/, "");
    case "paren":
    case "parens":
      return svalue ? `(${svalue})` : svalue;
    case "prefix":
      return (filterArg || "") + svalue;
    case "query":
      return svalue.replace(/\|\$.*\$\|/, '|$...$|').replace(/^query:/, '');
    case "sort":
      return ([...obj] || []).sort(naturalSort);
    case "similarity":
      const n = parseFloat((svalue || "0").replace(",", "."));
      const stype = entity['sim-type'] ? ` (${entity['sim-type']})` : '';
      return `${(n >= 2 ? n - 2 : n).toFixed(2)}${stype}`;
    case "size":
      size = ` width="${filterArg}" height="${filterArg}" `;
      if (svalue.indexOf("/>") !== -1) {
        return svalue.replace("/>", size + "/>");
      }
      return svalue.replace(/^([^>]+)>/, `$1 ${size}>`);
    case "suffix":
      return svalue + (filterArg || "");
    case "starrating":
      const starCount = parseInt(svalue || "1");
      return `${STAR * starCount}${STAR_OUTLINE * (5 - starCount)}`;
    case "sentence":
      return svalue && svalue.length ? `${svalue.charAt(0).toUpperCase()}${svalue.slice(1)}.` : "";
    case "title":
      return `<div class="sr-desc">
                <span class="block-title">${quote(filterArg)}</span>
                <span>${markText(quote(svalue), true)}</span>
              </div>`;
    case "tooltip":
      if (svalue.indexOf("/>" !== -1)) {
        return svalue.replace("/>", filterArg);
      }
      if (svalue.indexOf(">") !== -1) {
        return svalue.replace(/^([^>]+)>/, `$1 ${filterArg}>`);
      }
      return `<span title="${filterArg}">${markText(quote(svalue), true)}</span>`;
    case "truncate":
      const charLimit = parseInt(filterArg, 10);
      // If we have a match term and the text is long, show a window around the match
      const matchTermForTruncate = entity?.['match-term'] || entity?.['match-value'];
      if (matchTermForTruncate && svalue.length > charLimit) {
        // Find the position of the match in the text (case-insensitive)
        const lowerText = svalue.toLowerCase();
        const lowerMatch = matchTermForTruncate.toLowerCase();
        const matchPos = lowerText.indexOf(lowerMatch);

        if (matchPos !== -1) {
          // Calculate window around the match
          const matchEnd = matchPos + matchTermForTruncate.length;
          const halfWindow = Math.floor((charLimit - matchTermForTruncate.length) / 2);

          let start = Math.max(0, matchPos - halfWindow);
          let end = Math.min(svalue.length, matchEnd + halfWindow);

          // Adjust if we're at the beginning or end
          if (start === 0) {
            end = Math.min(svalue.length, charLimit);
          } else if (end === svalue.length) {
            start = Math.max(0, svalue.length - charLimit);
          }

          const prefix = start > 0 ? '...' : '';
          const suffix = end < svalue.length ? '...' : '';
          return `${prefix}${svalue.substring(start, end)}${suffix}`;
        }
      }
      // Fallback to simple truncation
      return svalue.length + 3 > filterArg ? `${svalue.substring(0, charLimit)}...` : svalue;
    case "unique":
      const current = svalue.toLowerCase();
      if (uniqueValues.has(current)) {
        return NOVALUE;
      }
      // Check for prefix matches (partial title matches, usually)
      for (const value of uniqueValues.values()) {
        if (value.toLowerCase().startsWith(current)) {
          return NOVALUE;
        }
      }
      return svalue;
    case "upper":
      return svalue.toUpperCase();
    case "urlencode":
      return svalue ? encodeURIComponent(svalue) : svalue;
    case "xof":
      return svalue.indexOf("gain") !== -1 ? "GOF" : svalue.indexOf("loss") !== -1 ? "LOF" : svalue;
    case "year":
      try {
        const dateFromObj = new Date(obj);
        if (dateFromObj.toString() !== "Invalid Date") {
          return dateFromObj.getFullYear();
        }
      } catch (err) {
      }
      return NOVALUE;
    default:
      return svalue;
  }
};
